import {
  TableBodyContainer,
  TableBodyLine,
  TableContainer,
  TableHeader
} from '@touche-lts/panel-ui/dist'
import React, { useEffect, useState } from 'react'
import tableBodyContainerStyle from '../../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'
import styles from './SportPollTable.module.scss'
import {
  SportPollTableHeaders,
  SportPollTableProps,
  getSportPollTableWidth
} from './types'
import { SPORT_POLL_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import { IconBin, IconEdit } from 'assets'
import { Button } from 'components/shared'
import { Option, SportPoll } from '../SportForm/types'

const SportPollTable = (props: SportPollTableProps) => {
  const {
    [SPORT_POLL_STORE]: { fetchSportPoll, deletePoll, sportPollList, clearStore },
    onEditSportForm
  } = props

  const projectId = window.location.pathname.split('/').pop()
  const [tableWidths, setTableWidths] = useState(getSportPollTableWidth())

  const getSportPoll = async () => {
    await fetchSportPoll(projectId)
  }

  // Helper to get width props based on screen size
  const getWidthProp = (columnKey: keyof typeof tableWidths.widths) => {
    const value = tableWidths.widths[columnKey]
    return tableWidths.usePercentage
      ? { widthPercentage: value }
      : { widthPx: value }
  }

  useEffect(() => {
    getSportPoll()

    // Handle window resize for responsive widths
    const handleResize = () => {
      const newWidths = getSportPollTableWidth()
      const isSameWidths = JSON.stringify(newWidths) === JSON.stringify(tableWidths)

      if (!isSameWidths) {
        setTableWidths(newWidths)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      clearStore()
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  function onDelete (pollId : number) {
    deletePoll(pollId)
  }

  function onEdit (sportPoll:SportPoll) {
    onEditSportForm(sportPoll)
  }

  return (
    <>
      {sportPollList.length > 0 && (
        <TableContainer
          style={{ overflow: 'auto', margin: '15px 0px' }}
          className={styles.thinScroll}
        >
          <TableHeader
            tableColumns={[
              {
                label: SportPollTableHeaders.START_TIME,
                ...getWidthProp('START_TIME')
              },
              {
                label: SportPollTableHeaders.END_TIME,
                ...getWidthProp('END_TIME')
              },
              {
                label: SportPollTableHeaders.RESULT_TIME,
                ...getWidthProp('RESULT_TIME')
              },
              {
                label: SportPollTableHeaders.QUESTION,
                ...getWidthProp('QUESTION')
              },
              {
                label: SportPollTableHeaders.OPTIONS,
                ...getWidthProp('OPTIONS')
              },
              {
                label: SportPollTableHeaders.ANSWER,
                ...getWidthProp('ANSWER')
              },
              {
                label: SportPollTableHeaders.ACTION,
                ...getWidthProp('ACTION')
              }
            ]}
          />
          <TableBodyContainer
            className={tableBodyContainerStyle.tableBodyContainer}
            style={tableWidths.usePercentage ? { width: '100%' } : {}}
          >
            {sportPollList.map((sportPoll) => {
              return (
                <TableBodyLine
                  key={sportPoll?.pollId}
                  className={styles.customTableLine}
                  cells={[
                    {
                      children: <p>{sportPoll.startTime + ' sec'}</p>,
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('START_TIME')
                    },
                    {
                      children: <p>{sportPoll.endTime + ' sec'}</p>,
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('END_TIME')
                    },
                    {
                      children: <p>{sportPoll.resultTime + ' sec'}</p>,
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('RESULT_TIME')
                    },
                    {
                      children: <p>{sportPoll.question}</p>,
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('QUESTION')
                    },
                    {
                      children: (
                        <p>
                          {sportPoll.options.map(
                            (option: Option) => `${option.optionValue}, `
                          )}
                        </p>
                      ),
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('OPTIONS')
                    },
                    {
                      children: <p>{sportPoll.answer}</p>,
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('ANSWER')
                    },
                    {
                      children: (
                        <div className={styles.actionButton}>
                          <Button
                            preset='icon'
                            onClick={() => onEdit(sportPoll)}
                          >
                            <IconEdit />
                          </Button>

                          <Button
                            preset='icon'
                            onClick={() => onDelete(sportPoll.pollId)}
                          >
                            <IconBin />
                          </Button>
                        </div>
                      ),
                      style: { flexWrap: 'wrap' },
                      ...getWidthProp('ACTION')
                    }
                  ]}
                />
              )
            })}
          </TableBodyContainer>
        </TableContainer>
      )}
    </>
  )
}

export default inject(SPORT_POLL_STORE)(observer(SportPollTable))
